import React, { useEffect, useId, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import {
  HiOutlineExclamation,
  HiOutlineMinus,
  HiOutlinePlus,
  HiOutlineShieldCheck,
  HiOutlineX,
} from "react-icons/hi";

import Button from "../../components/Button";
import ToastNotification from "../../components/ToastNotification";
import {
  adjustMemberCredits,
  getCreditsAdjustRefusal,
  getErrorStatus,
  newCreditsIdempotencyKey,
} from "../../api/memberCredits/actions";
import { memberCreditsQueryKey } from "../../api/memberCredits/queryKeys";
import {
  AboCreditAdjustmentResponse,
  CREDITS_ADJUST_BALANCE_CHANGED,
  CREDITS_ADJUST_KEY_CONFLICT,
  CREDITS_ADJUST_OPERATOR_UNVERIFIED,
  CREDITS_ADJUST_REASON_MAX_LENGTH,
  CreditsAdjustRefusalCode,
} from "../../api/memberCredits/types";
import { useActorEmail } from "../../hooks/useActorEmail";
import { useOperatorIdentity } from "../../hooks/useOperatorIdentity";
import { useOperatorToken } from "../../hooks/useOperatorToken";
import { formatCount } from "../../utils/formatCount";
import {
  memberCreditsStrings as S,
  formatMessage,
} from "./MemberCreditsTab.strings";
import {
  adjustmentResultMessage,
  formatCredits,
  formatSignedCredits,
} from "./memberCreditsFormat";

/** React Query key prefix of the Customer Information list. */
const CUSTOMER_LIST_QUERY_KEY = "abo-customer-search";

/** The quick amounts offered under the amount field; each click adds its value. */
const QUICK_AMOUNTS = [1, 5, 10, 25];

const REASON_CATEGORIES = Object.values(S.adjust.reasonCategories);

type Direction = "add" | "remove";

type Step = "form" | "confirm";

type Feedback =
  | { kind: "refusal"; code: CreditsAdjustRefusalCode }
  | { kind: "invalid" }
  | { kind: "error" };

interface MemberCreditsAdjustDrawerProps {
  open: boolean;
  customerId: string;
  memberName: string;
  /** The balance shown on the tab, restated in the preview and confirmation. */
  balance: number;
  /** Largest |amount| DS accepts on the ABO route. */
  adjustmentLimit: number;
  onClose: () => void;
  /** Called once DS applied (or replayed) the adjustment. */
  onApplied: (result: AboCreditAdjustmentResponse) => void;
}

const feedbackMessage = (feedback: Feedback): string => {
  switch (feedback.kind) {
    case "refusal":
      return S.adjust.refusals[feedback.code];
    case "invalid":
      return S.adjust.invalid;
    default:
      return S.adjust.error;
  }
};

/** A whole number from 1 to `max`, or `null`. */
const parseAmount = (value: string, max: number): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const amount = Number(trimmed);
  return amount >= 1 && amount <= max ? amount : null;
};

/**
 * The amount after a quick pick: the typed whole number (0 when the field is
 * empty or not a whole number) plus `step`, clamped to `max`.
 */
const addQuickAmount = (
  value: string,
  step: number,
  max: number
): number => {
  const trimmed = value.trim();
  const current = /^\d+$/.test(trimmed) ? Number(trimmed) : 0;
  return Math.min(current + step, max);
};

/** The reason DS stores: "<category>: <note>", or whichever one is given. */
const composeReason = (category: string | null, note: string): string => {
  const trimmedNote = note.trim();
  if (category && trimmedNote) {
    return `${category}: ${trimmedNote}`;
  }
  return category ?? trimmedNote;
};

const chipClass = (active: boolean): string =>
  `min-h-[36px] rounded-full border px-3 text-sm font-semibold transition-colors ${
    active
      ? "border-primary-500 bg-primary-50 text-primary-700"
      : "border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100"
  }`;

/**
 * Role-gated side drawer of the Credits tab (abo-member-credits, redesign
 * option 1): Add/Remove, amount with quick picks, a reason category plus a
 * note, a live "balance now → after" preview, then a review step that
 * restates the change; only Confirm calls DS.
 *
 * Idempotency: one key `abo-<uuid>` per confirmed payload. A retry of the same
 * payload (after `CREDITS_ADJUST_BALANCE_CHANGED`, a network error or any
 * other failure, also after closing and reopening the drawer) reuses it, so
 * DS replays instead of applying twice; a success or
 * `CREDITS_ADJUST_KEY_CONFLICT` drops it, and a changed amount or reason gets
 * a new one.
 *
 * The operator's FusionAuth token is read (after a refresh) at Confirm time
 * and handed to that one request: never kept in state, cache or logs.
 */
const MemberCreditsAdjustDrawer: React.FC<MemberCreditsAdjustDrawerProps> = ({
  open,
  customerId,
  memberName,
  balance,
  adjustmentLimit,
  onClose,
  onApplied,
}) => {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const operatorEmail = useActorEmail();
  const readOperator = useOperatorIdentity();
  const { getOperatorToken, signInAgain } = useOperatorToken();

  const [direction, setDirection] = useState<Direction>("add");
  const [amountInput, setAmountInput] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  // The key of the payload being confirmed, with that payload's fingerprint.
  const pendingKeyRef = useRef<{ key: string; fingerprint: string } | null>(
    null
  );

  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
    }
  }, [open]);

  const amount = parseAmount(amountInput, adjustmentLimit);
  const reason = composeReason(category, note);
  const amountError =
    amount === null
      ? formatMessage(S.adjust.validation.amount, { max: adjustmentLimit })
      : null;
  const reasonError = !reason
    ? S.adjust.validation.reasonRequired
    : reason.length > CREDITS_ADJUST_REASON_MAX_LENGTH
    ? formatMessage(S.adjust.validation.reasonTooLong, {
        max: CREDITS_ADJUST_REASON_MAX_LENGTH,
      })
    : null;
  const isValid = !amountError && !reasonError;
  const signedAmount =
    amount === null ? 0 : direction === "remove" ? -amount : amount;
  const expectedBalance = Math.max(0, balance + signedAmount);
  const clamps = direction === "remove" && amount !== null && amount > balance;
  const atLimit = amount === adjustmentLimit;

  const invalidateCredits = () =>
    queryClient.invalidateQueries(memberCreditsQueryKey(customerId));

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setStep("form");
    onClose();
  };

  const handleReview = () => {
    setShowErrors(true);
    if (!isValid) {
      return;
    }
    const fingerprint = `${signedAmount}|${reason}`;
    if (pendingKeyRef.current?.fingerprint !== fingerprint) {
      pendingKeyRef.current = { key: newCreditsIdempotencyKey(), fingerprint };
    }
    setFeedback(null);
    setStep("confirm");
  };

  const handleBack = () => {
    if (submitting) {
      return;
    }
    setStep("form");
    setFeedback(null);
  };

  const resetForm = () => {
    setAmountInput("");
    setCategory(null);
    setNote("");
    setShowErrors(false);
    setStep("form");
  };

  const handleFailure = (error: unknown) => {
    const refusal = getCreditsAdjustRefusal(error);
    if (!refusal) {
      setFeedback({ kind: getErrorStatus(error) === 400 ? "invalid" : "error" });
      return;
    }
    if (refusal.code === CREDITS_ADJUST_KEY_CONFLICT) {
      // Start again: a new Review gets a new key.
      pendingKeyRef.current = null;
      setStep("form");
    }
    if (refusal.code === CREDITS_ADJUST_BALANCE_CHANGED) {
      // Refresh the balance the confirmation restates; the retry keeps the key.
      void invalidateCredits();
    }
    setFeedback({ kind: "refusal", code: refusal.code });
  };

  const handleConfirm = async () => {
    const pending = pendingKeyRef.current;
    if (!pending || !isValid || submitting) {
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const token = await getOperatorToken();
      const data = await adjustMemberCredits(
        customerId,
        { amount: signedAmount, reason, idempotencyKey: pending.key },
        { ...readOperator(), token }
      );
      pendingKeyRef.current = null;
      resetForm();
      ToastNotification({
        type: "success",
        message: adjustmentResultMessage(data),
      });
      void invalidateCredits();
      void queryClient.invalidateQueries(CUSTOMER_LIST_QUERY_KEY);
      onApplied(data);
    } catch (error: unknown) {
      handleFailure(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const feedbackAlert = feedback && (
    <div
      className="flex items-start gap-2 rounded-lg bg-red-50 p-3"
      role="alert"
      data-testid="credits-adjust-feedback"
      data-code={feedback.kind === "refusal" ? feedback.code : feedback.kind}
    >
      <HiOutlineExclamation className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-red-800">{feedbackMessage(feedback)}</p>
        {feedback.kind === "refusal" &&
          feedback.code === CREDITS_ADJUST_OPERATOR_UNVERIFIED && (
            <Button variant="DARK" size="xs" onClick={signInAgain}>
              {S.adjust.signInAgain}
            </Button>
          )}
      </div>
    </div>
  );

  const confirmBody = formatMessage(
    direction === "remove" ? S.adjust.confirm.removeBody : S.adjust.confirm.addBody,
    { amount: formatCredits(amount ?? 0), name: memberName }
  );

  const reviewLabel =
    amount === null
      ? S.adjust.review
      : formatMessage(
          direction === "remove" ? S.adjust.reviewRemove : S.adjust.reviewAdd,
          { amount: formatCredits(amount) }
        );

  const form = (
    <div className="flex flex-col gap-5">
      {feedbackAlert}

      <fieldset>
        <legend className="sr-only">{S.adjust.directionLabel}</legend>
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-neutral-100 p-1">
          {(["add", "remove"] as Direction[]).map((value) => {
            const active = direction === value;
            const Icon = value === "add" ? HiOutlinePlus : HiOutlineMinus;
            return (
              <label
                key={value}
                className={`flex min-h-[40px] cursor-pointer items-center justify-center gap-2 rounded-lg text-sm font-semibold focus-within:ring-2 focus-within:ring-primary-400 ${
                  active
                    ? "bg-white text-neutral-900 shadow-sm"
                    : "text-neutral-500"
                }`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  name={`credits-direction-${customerId}`}
                  value={value}
                  checked={active}
                  disabled={submitting}
                  onChange={() => setDirection(value)}
                />
                <Icon className="h-4 w-4" aria-hidden="true" />
                {value === "add" ? S.adjust.add : S.adjust.remove}
              </label>
            );
          })}
        </div>
      </fieldset>

      <div>
        <label
          htmlFor="credits-adjust-amount"
          className="mb-1.5 block text-sm font-semibold text-neutral-900"
        >
          {S.adjust.amountLabel}
        </label>
        <input
          id="credits-adjust-amount"
          data-testid="credits-adjust-amount"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-base text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          value={amountInput}
          disabled={submitting}
          aria-invalid={showErrors && Boolean(amountError)}
          onChange={(event) => setAmountInput(event.target.value)}
        />
        {showErrors && amountError ? (
          <p
            className="mt-1 text-xs text-red-700"
            data-testid="credits-adjust-amount-error"
          >
            {amountError}
          </p>
        ) : atLimit ? (
          <p
            className="mt-1 text-xs text-amber-800"
            data-testid="credits-adjust-amount-limit"
            aria-live="polite"
          >
            {formatMessage(S.adjust.amountLimitReached, { max: adjustmentLimit })}
          </p>
        ) : (
          <p className="mt-1 text-xs text-neutral-500">
            {formatMessage(S.adjust.amountHint, { max: adjustmentLimit })}
          </p>
        )}
        <div
          role="group"
          aria-label={S.adjust.quickAmounts}
          className="mt-2 flex flex-wrap gap-2"
        >
          {QUICK_AMOUNTS.filter((value) => value <= adjustmentLimit).map(
            (value) => (
              <button
                key={value}
                type="button"
                disabled={submitting || atLimit}
                className={`${chipClass(false)} disabled:cursor-not-allowed disabled:opacity-50`}
                onClick={() =>
                  setAmountInput((current) =>
                    String(addQuickAmount(current, value, adjustmentLimit))
                  )
                }
              >
                {formatSignedCredits(direction === "remove" ? -value : value)}
              </button>
            )
          )}
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-sm font-semibold text-neutral-900">
          {S.adjust.reasonLabel}
        </p>
        <div
          role="group"
          aria-label={S.adjust.reasonLabel}
          className="flex flex-wrap gap-2"
          data-testid="credits-adjust-categories"
        >
          {REASON_CATEGORIES.map((value) => (
            <button
              key={value}
              type="button"
              disabled={submitting}
              aria-pressed={category === value}
              className={chipClass(category === value)}
              onClick={() =>
                setCategory((current) => (current === value ? null : value))
              }
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label
          htmlFor="credits-adjust-reason"
          className="mb-1.5 block text-sm font-semibold text-neutral-900"
        >
          {S.adjust.noteLabel}
          <span className="font-normal text-neutral-500"> · {S.adjust.noteHint}</span>
        </label>
        <textarea
          id="credits-adjust-reason"
          data-testid="credits-adjust-reason"
          rows={3}
          className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          placeholder={S.adjust.reasonPlaceholder}
          value={note}
          disabled={submitting}
          aria-invalid={showErrors && Boolean(reasonError)}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className="mt-1 flex justify-between gap-2">
          <p
            className="text-xs text-red-700"
            data-testid="credits-adjust-reason-error"
          >
            {showErrors ? reasonError : null}
          </p>
          <p className="text-xs text-neutral-500">
            {formatMessage(S.adjust.reasonCounter, {
              count: reason.length,
              max: CREDITS_ADJUST_REASON_MAX_LENGTH,
            })}
          </p>
        </div>
      </div>

      {amount !== null && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl bg-neutral-100 px-4 py-3"
          data-testid="credits-adjust-preview"
        >
          <div>
            <p className="text-xs text-neutral-500">{S.adjust.preview.now}</p>
            <p className="text-2xl font-bold tabular-nums">{formatCount(balance)}</p>
          </div>
          <p
            className={`text-base font-bold ${
              direction === "remove" ? "text-red-700" : "text-emerald-800"
            }`}
          >
            {formatSignedCredits(clamps ? -balance : signedAmount)}
          </p>
          <div className="text-right">
            <p className="text-xs text-neutral-500">{S.adjust.preview.after}</p>
            <p className="text-2xl font-bold tabular-nums">
              {formatCount(expectedBalance)}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const confirm = (
    <div
      className="flex flex-col gap-3 text-sm text-neutral-700"
      data-testid="credits-adjust-confirm"
    >
      <p className="text-base font-semibold text-neutral-900">{confirmBody}</p>
      <p data-testid="credits-adjust-confirm-balance">
        {formatMessage(S.adjust.confirm.balanceChange, {
          before: formatCount(balance),
          after: formatCount(expectedBalance),
        })}
      </p>
      {clamps && (
        <p
          className="rounded-lg bg-amber-50 p-3 text-amber-900"
          data-testid="credits-adjust-confirm-clamp"
        >
          {formatMessage(S.adjust.confirm.clampWarning, {
            balance: formatCount(balance),
          })}
        </p>
      )}
      <p className="whitespace-pre-wrap break-words">
        {formatMessage(S.adjust.confirm.reason, { reason })}
      </p>
      {feedbackAlert}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          handleClose();
        }
      }}
    >
      <div
        className="absolute inset-0 bg-neutral-900/40"
        aria-hidden="true"
        onClick={handleClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="credits-adjust-panel"
        className="relative flex h-full w-full max-w-[440px] flex-col bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-6 py-5">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-neutral-900">
              {step === "confirm" ? S.adjust.confirm.title : S.adjust.heading}
            </h2>
            <p className="mt-0.5 text-sm text-neutral-500">
              {formatMessage(S.adjust.subtitle, {
                name: memberName,
                max: adjustmentLimit,
              })}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label={S.adjust.close}
            disabled={submitting}
            onClick={handleClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"
          >
            <HiOutlineX className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "confirm" ? confirm : form}
        </div>

        <div className="flex flex-col gap-2 border-t border-neutral-200 px-6 py-4">
          {step === "confirm" ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="LIGHT" disabled={submitting} onClick={handleBack}>
                {S.adjust.confirm.cancel}
              </Button>
              <Button
                variant="DARK"
                isProcessing={submitting}
                disabled={submitting}
                onClick={() => void handleConfirm()}
              >
                {feedback ? S.adjust.confirm.retry : S.adjust.confirm.confirm}
              </Button>
            </div>
          ) : (
            <Button
              variant="DARK"
              className="w-full"
              disabled={submitting}
              onClick={handleReview}
            >
              {reviewLabel}
            </Button>
          )}
          {operatorEmail && (
            <p className="flex items-center justify-center gap-1.5 text-xs text-neutral-500">
              <HiOutlineShieldCheck className="h-4 w-4 text-emerald-700" aria-hidden="true" />
              {formatMessage(S.adjust.recordedAs, { email: operatorEmail })}
            </p>
          )}
        </div>
      </aside>
    </div>
  );
};

export default MemberCreditsAdjustDrawer;
